'use client'

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

export interface LicenseMapTooltipAnchor {
  abbr: string
  /** Position inside the widget root (which is position: relative). */
  x: number
  y: number
  /** Open downward when the anchor sits too close to the root's top edge. */
  below: boolean
}

const HIDE_DELAY_MS = 250
/** Half the tooltip's max-width (see license-map.css) — keeps it inside the root horizontally. */
const HALF_WIDTH = 130
const FLIP_BELOW_Y = 150

// Tooltip state for the license map. The anchor is captured once on entry and
// then held still — it must not chase the cursor, or the "View source" link
// inside it is unreachable. Hiding runs on a short delay for the same reason:
// the cursor needs time to travel from the state onto the tooltip.
export const useLicenseMapTooltip = (rootRef: RefObject<HTMLElement | null>) => {
  const [active, setActive] = useState<LicenseMapTooltipAnchor | null>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelHide = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
  }, [])
  const hide = useCallback(() => {
    cancelHide()
    setActive(null)
  }, [cancelHide])
  const scheduleHide = useCallback(() => {
    cancelHide()
    hideTimer.current = setTimeout(() => setActive(null), HIDE_DELAY_MS)
  }, [cancelHide])

  const showAt = useCallback(
    (el: Element, clientX: number, clientY: number) => {
      const root = rootRef.current
      const abbr = el.getAttribute('data-abbr')
      if (!root || !abbr) {
        return
      }
      cancelHide()
      const rect = root.getBoundingClientRect()
      const maxX = Math.max(rect.width - HALF_WIDTH, HALF_WIDTH)
      const x = Math.min(Math.max(clientX - rect.left, HALF_WIDTH), maxX)
      const y = clientY - rect.top
      setActive({ abbr, x, y, below: y < FLIP_BELOW_Y })
    },
    [cancelHide, rootRef]
  )
  const showFor = useCallback(
    (el: Element) => {
      const r = el.getBoundingClientRect()
      showAt(el, r.left + r.width / 2, r.top + r.height / 2)
    },
    [showAt]
  )

  // Highlight the anchored state; dismiss on Escape or a pointer-down outside the widget.
  useEffect(() => {
    if (!active) {
      return
    }
    const root = rootRef.current
    const el = root?.querySelector(`.state[data-abbr="${active.abbr}"]`)
    el?.classList.add('is-active')
    const onPointerDown = (e: PointerEvent) => {
      if (root && e.target instanceof Node && !root.contains(e.target)) {
        hide()
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        hide()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      el?.classList.remove('is-active')
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [active, hide, rootRef])

  useEffect(() => cancelHide, [cancelHide])

  return { active, cancelHide, scheduleHide, hide, showAt, showFor }
}
